/**
 * useCollections Hook
 * 
 * React hook for fetching and managing collections data.
 */

import { useState, useEffect, useCallback } from 'react';
import { getCollectionService } from '../../infrastructure/services/collection.service';
import {
  CollectionEntity,
  CollectionWithProducts,
  SearchFilters,
  CollectionPagination,
} from '../../domain/entities/collection.entity';
import { storefrontLayoutLogger } from '@/lib/logger';
import { usePricingContext } from '@/lib/hooks/use-pricing-context';

function stableFiltersKey(filters: SearchFilters): string {
  return JSON.stringify(
    Object.keys(filters)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = filters[key as keyof SearchFilters] as unknown;
        return acc;
      }, {}),
  );
}

interface UseCollectionsResult {
  collections: CollectionEntity[];
  pagination: CollectionPagination | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useCollections(page = 1, limit = 20): UseCollectionsResult {
  const [collections, setCollections] = useState<CollectionEntity[]>([]);
  const [pagination, setPagination] = useState<CollectionPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCollections = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const service = getCollectionService();
      const result = await service.getCollections(page, limit);
      setCollections(result.collections);
      setPagination(result.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch collections');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  return {
    collections,
    pagination,
    loading,
    error,
    refresh: fetchCollections,
  };
}

interface UseCollectionResult {
  data: CollectionWithProducts | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  updateFilters: (filters: SearchFilters) => void;
}

export function useCollection(slug: string, filters: SearchFilters = {}): UseCollectionResult {
  const { currency, country } = usePricingContext();
  const [data, setData] = useState<CollectionWithProducts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filtersWithPricing = { ...filters, currency, country };
  const filtersKey = stableFiltersKey(filtersWithPricing);

  const fetchCollection = useCallback(async () => {
    if (!slug) return;

    setLoading(true);
    setError(null);

    try {
      const service = getCollectionService();
      const result = await service.getCollectionBySlug(slug, filtersWithPricing);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch collection');
    } finally {
      setLoading(false);
    }
  }, [slug, filtersKey, currency, country]);

  useEffect(() => {
    fetchCollection();
  }, [fetchCollection]);

  const updateFilters = useCallback((newFilters: SearchFilters) => {
    // This is now a no-op since filters come from props
    storefrontLayoutLogger.info('updateFilters called on useCollectionBySlug no-op path', {
      hasFilters: Object.keys(newFilters).length > 0,
    });
  }, []);

  return {
    data,
    loading,
    error,
    refresh: fetchCollection,
    updateFilters,
  };
}

interface UseSearchResult {
  data: CollectionWithProducts | null;
  loading: boolean;
  error: string | null;
  search: (filters: SearchFilters) => void;
}

export function useSearch(initialFilters: SearchFilters = {}): UseSearchResult {
  const { currency, country } = usePricingContext();
  const [data, setData] = useState<CollectionWithProducts | null>(null);
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const performSearch = useCallback(async (searchFilters: SearchFilters) => {
    setFilters(searchFilters);
    setLoading(true);
    setError(null);

    try {
      const service = getCollectionService();
      const result = await service.searchProducts({ ...searchFilters, currency, country });
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search products');
    } finally {
      setLoading(false);
    }
  }, [currency, country]);

  // Initial search if filters provided
  useEffect(() => {
    if (initialFilters.q || Object.keys(initialFilters).length > 0) {
      performSearch(initialFilters);
    }
  }, []);

  return {
    data,
    loading,
    error,
    search: performSearch,
  };
}
